import { useRecoilState } from 'recoil';
import { frontendSettingsAtom } from '../../../atoms/FrontendSettings';
import { ComponentStyle } from '../../../interfaces/Settings/SettingsV2/ComponentStyle';
import { getFrontendSettings, upsertSettings } from '../../../api/Settings';
import { SettingsType } from '../../../interfaces/Settings/SettingsType';

export const getValueByPath = (obj: unknown, path: string) => {
  if (!obj || !path) return undefined;

  return path.split('.').reduce<any>((acc, key) => acc?.[key], obj);
};

export const setValueByPath = <T extends object>(
  obj: T,
  path: string,
  value: unknown,
): T => {
  const keys = path.split('.');
  const result: any = Array.isArray(obj) ? [...obj] : { ...obj };

  let current: any = result;
  let originalCurrent: any = obj;

  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    const nextOriginalValue = originalCurrent?.[key];

    current[key] = Array.isArray(nextOriginalValue)
      ? [...nextOriginalValue]
      : { ...(nextOriginalValue ?? {}) };

    current = current[key];
    originalCurrent = nextOriginalValue;
  }

  current[keys[keys.length - 1]] = value;

  return result;
};

export const useEditableComponentStyle = (path: string | null) => {
  const [settings, setSettings] = useRecoilState(frontendSettingsAtom);

  const value = path
    ? (getValueByPath(settings.settingsV2 ?? {}, path) as
        | ComponentStyle
        | undefined)
    : undefined;

  const setValue = async (newStyle: ComponentStyle, submit: boolean) => {
    if (!path) return;

    const newSettingsV2 = setValueByPath(
      settings.settingsV2 ?? {},
      path,
      newStyle,
    );
    if (submit) {
      await upsertSettings([
        {
          type: SettingsType.FRONTEND,
          key: 'style_overrides',
          genericValue: { stringValue: JSON.stringify(newSettingsV2) },
          createdAt: new Date(),
          updatedAt: new Date(),
        },
      ]);
      setSettings(await getFrontendSettings());

      return;
    }

    setSettings((prev) => ({
      ...prev,
      settingsV2: setValueByPath(prev.settingsV2 ?? {}, path, newStyle),
    }));
  };

  return {
    value: value ?? {},
    setValue,
  };
};
